import colorHelpers from 'tinycolor2';
import {
  drawNode,
  addToNode,
  createRect,
  createLayer,
  attachEvent,
  setNodeAttrs,
  getNodeAttrs,
  addNodeIntoLayer,
  isInstanceofGroup,
  getNodeContainer,
} from '@picsart/web-layering/rendering/konva/selectors';
import {
  CanvasLayer, Stage, Rect, Vector2d, Group,
} from '@picsart/web-layering/types';

import { scrollBarsLayerId } from 'configs/constants';

import { batchDrawNode } from './selectors';

const getChangedPos = (stageSize: number, containerSize: number, scrollBarSize: number, scrollBarPos: number, scale: number): number => {
  const availableHeight = containerSize - (scrollBarSize * scale);
  const delta = scrollBarPos / availableHeight;

  return -(stageSize - containerSize) * delta;
};

interface IAddScrollLayersToCanvasProps {
  stage: Stage;
  layers: CanvasLayer[] | Group;
  scale: number;
  currentContainerWidth: number;
  currentContainerHeight: number;
}

const defaultSizeBar = 6;

const addScrollLayersToCanvas = ({
  stage, layers, scale, currentContainerWidth, currentContainerHeight,
}: IAddScrollLayersToCanvasProps) => {
  const { height, width } = getNodeAttrs(isInstanceofGroup(layers) ? layers : stage);

  const isShowVerticalBar = height > currentContainerHeight;
  const isShowHorizontalBar = width > currentContainerWidth;

  if (!isShowVerticalBar && !isShowHorizontalBar) {
    return;
  }

  const scrollLayers = createLayer({ id: scrollBarsLayerId });
  addToNode(stage, scrollLayers);

  const { _canvas } = scrollLayers.getCanvas();
  _canvas.style.pointerEvents = 'none';

  let verticalBar: Rect;
  const verticalBarHeight = currentContainerHeight * currentContainerHeight / height / scale;

  const { width: scrollLayerWidth, height: scrollLayerHeight } = getNodeAttrs(scrollLayers);

  if (isShowVerticalBar) {
    const minWidth = Math.min(currentContainerWidth, width);
    verticalBar = createRect({
      width: defaultSizeBar / scale,
      height: verticalBarHeight,
      fill: colorHelpers('#282b3699').toRgbString(),
      opacity: 0.8,
      x: (scrollLayerWidth - defaultSizeBar) / scale,
      y: 0,
      cornerRadius: 14,
      shadowColor: colorHelpers('#282b3614').toRgbString(),
      shadowOffsetX: 0,
      shadowOffsetY: 2,
      shadowBlur: 4,
      draggable: true,
      dragBoundFunc(pos: Vector2d) {
        const newPos = { ...pos };
        newPos.x = minWidth - defaultSizeBar;
        newPos.y = Math.max(
          Math.min(newPos.y, currentContainerHeight - verticalBarHeight * scale),
          0,
        );
        return newPos;
      },
    });
    addNodeIntoLayer(scrollLayers, verticalBar);

    // for Eraser and ObRemover
    attachEvent(verticalBar, 'mousedown', e => {
      e.cancelBubble = true;
    });

    let lastVerticalCursor = '';

    attachEvent(verticalBar, 'mousemove', e => {
      e.evt.onScrollBar = true;

      const containerStyles = getNodeContainer(stage);

      if (!lastVerticalCursor) {
        lastVerticalCursor = containerStyles.style.cursor || 'none';
      }

      containerStyles.style.setProperty('cursor', 'default');
    });

    attachEvent(verticalBar, 'mouseleave', () => {
      getNodeContainer(stage).style.setProperty('cursor', lastVerticalCursor || 'default');
    });
    // end Eraser and ObRemover

    attachEvent(verticalBar, 'dragmove', () => {
      const { height: layerHeight } = getNodeAttrs(isInstanceofGroup(layers) ? layers : stage);
      const { y } = getNodeAttrs(verticalBar);

      const newY = getChangedPos(layerHeight, currentContainerHeight, verticalBarHeight, y, scale);

      if (isInstanceofGroup(layers)) {
        setNodeAttrs(layers, { y: newY });
        drawNode(layers);
        batchDrawNode(layers);
      } else {
        layers.forEach((layer: CanvasLayer) => {
          setNodeAttrs(layer, { y: newY });
        });
        batchDrawNode(stage);
      }
    });
  }

  let horizontalBar: Rect;
  const horizontalBarWidth = currentContainerWidth * currentContainerWidth / (width * scale) / scale;

  if (isShowHorizontalBar) {
    horizontalBar = createRect({
      width: horizontalBarWidth,
      height: defaultSizeBar / scale,
      fill: colorHelpers('#282b3699').toRgbString(),
      opacity: 0.8,
      shadowColor: colorHelpers('#282b3614').toRgbString(),
      x: 0,
      y: (scrollLayerHeight - defaultSizeBar) / scale,
      cornerRadius: 14,
      shadowOffsetX: 0,
      shadowOffsetY: 2,
      shadowBlur: 4,
      draggable: true,
      dragBoundFunc(pos: Vector2d) {
        const newPos = { ...pos };
        newPos.x = Math.max(
          Math.min(newPos.x, currentContainerWidth - horizontalBarWidth * scale),
          0,
        );
        newPos.y = Math.min(currentContainerHeight, height) - defaultSizeBar;

        return newPos;
      },
    });

    addNodeIntoLayer(scrollLayers, horizontalBar);

    // for Eraser and ObRemover
    attachEvent(horizontalBar, 'mousedown', e => {
      e.cancelBubble = true;
    });

    let lastHorizontalCursor = '';

    attachEvent(horizontalBar, 'mousemove', e => {
      e.evt.onScrollBar = true;

      const containerStyle = getNodeContainer(stage);

      if (!lastHorizontalCursor) {
        lastHorizontalCursor = containerStyle.style.cursor || 'none';
      }

      containerStyle.style.setProperty('cursor', 'default');
    });

    attachEvent(horizontalBar, 'mouseleave', () => {
      getNodeContainer(stage).style.setProperty('cursor', lastHorizontalCursor || 'default');
    });
    // end Eraser and ObRemover

    attachEvent(horizontalBar, 'dragmove', () => {
      const { width: layerWidth } = getNodeAttrs(isInstanceofGroup(layers) ? layers : stage);
      const { x } = getNodeAttrs(horizontalBar);
      const newX = getChangedPos(layerWidth, currentContainerWidth, horizontalBarWidth, x, scale);

      if (isInstanceofGroup(layers)) {
        setNodeAttrs(layers, { x: newX });
        drawNode(layers);
        batchDrawNode(layers);
      } else {
        layers.forEach((layer: CanvasLayer) => {
          setNodeAttrs(layer, { x: newX });
        });
        batchDrawNode(stage);
      }
    });
  }

  drawNode(scrollLayers);

  attachEvent(stage, 'wheel.scroll', e => {
    e.evt.preventDefault();
    const dx = e.evt.deltaX / 2;
    const dy = e.evt.deltaY / 2;

    const nextLayersCoords: { x?: number; y?: number } = {};

    if (verticalBar) {
      const { y } = getNodeAttrs(verticalBar);
      const newVerticalBarY = Math.max(
        Math.min(y + dy, currentContainerHeight / scale - verticalBarHeight),
        0,
      );

      setNodeAttrs(verticalBar, { y: newVerticalBarY });

      nextLayersCoords.y = getChangedPos(getNodeAttrs(isInstanceofGroup(layers) ? layers : stage).height, currentContainerHeight, verticalBarHeight, newVerticalBarY, scale);
    }

    if (horizontalBar) {
      const { x } = getNodeAttrs(horizontalBar);
      const newHorizontalBarX = Math.max(
        Math.min(x + dx, currentContainerWidth / scale - horizontalBarWidth),
        0,
      );

      setNodeAttrs(horizontalBar, { x: newHorizontalBarX });

      nextLayersCoords.x = getChangedPos(getNodeAttrs(isInstanceofGroup(layers) ? layers : stage).width, currentContainerWidth, horizontalBarWidth, newHorizontalBarX, scale);
    }

    if (isInstanceofGroup(layers)) {
      setNodeAttrs((layers as Group), nextLayersCoords);
    } else {
      layers.forEach((layer: CanvasLayer) => {
        setNodeAttrs(layer, nextLayersCoords);
      });
    }

    batchDrawNode(stage);
  });
};

export default addScrollLayersToCanvas;
